import { useEffect, useState } from 'react'
import { Box, BoxProps, Chip, Divider, IconButton, styled, SxProps, Theme, Tooltip, Typography, TypographyProps } from '@mui/material'
import type { ComponentType, FC, PropsWithChildren, ReactNode } from 'react'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { dayjsCustom, mergeObjects } from '../utils'
import { CopyToClipboard, HelpTooltipWrap } from '../components'

//#region CellImageSmall
export type IStatusCellConfig<T extends string> = { [key in T]: SxProps<Theme> }

export interface IStatusCellOptions {
  sx?: SxProps<Theme>
}

export interface IStatusCellProps<T extends string> extends PropsWithChildren {
  value?: T
  title?: string
  sx?: SxProps<Theme>
  onClick?: TypographyProps['onClick']
  slots?: {
    typographyProps?: TypographyProps
  }
}

export const CreateStatusCell = function <T extends string>(
  config: IStatusCellConfig<T>,
  options?: IStatusCellOptions
): ComponentType<IStatusCellProps<T>> {
  const StatusCell: FC<IStatusCellProps<T>> = (props) => {
    const getSxProps = (): SxProps<Theme> => {
      const { value: data, sx, slots } = props
      return Object.assign({}, data ? config[data] : {}, options?.sx, sx, slots?.typographyProps?.sx)
    }

    if (!props.value) return <></>
    return (
      <CustomTypography onClick={props.onClick} {...props.slots?.typographyProps} sx={getSxProps()}>
        {props.title || props.value}
      </CustomTypography>
    )
  }
  return StatusCell
}

const CustomTypography = styled(Typography)({
  borderRadius: '4px',
  fontSize: '0.8rem',
  padding: '4px 9px',
  fontWeight: 600,
  minWidth: '70px',
  textAlign: 'center',
  color: '#ffffff',
  display: 'inline-block'
})

//#region CellImageSmall
interface CellImageSmallProps {
  value: string
  imageUri?: string
  fallbackSrc?: string
}

export const CellImageSmall: FC<CellImageSmallProps> = (props) => {
  const [imgSrc, setImgSrc] = useState(props.imageUri ?? props.fallbackSrc)
  useEffect(() => {
    setImgSrc(props.imageUri ?? props.fallbackSrc)
  }, [props.imageUri, props.fallbackSrc])
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: '10px' }}>
      <ImageSmall src={imgSrc} onError={() => setImgSrc(props.fallbackSrc)} />
      <Typography variant='body1'>{props.value}</Typography>
    </Box>
  )
}

interface ImageSmallFallbackProps {
  imageUri?: string
  imageFallback?: string
  sx?: SxProps<Theme>
}

export const ImageSmallFallback: FC<ImageSmallFallbackProps> = (props) => {
  const [imgSrc, setImgSrc] = useState(props.imageUri ?? props.imageFallback)
  useEffect(() => {
    setImgSrc(props.imageUri ?? props.imageFallback)
  }, [props.imageUri, props.imageFallback])
  return <ImageSmall src={imgSrc} onError={() => setImgSrc(props.imageFallback)} sx={props.sx} />
}

const ImageSmall = styled('img')({
  backgroundRepeat: 'no-repeat',
  height: '50px',
  width: '50px',
  backgroundSize: 'contain',
  borderRadius: '8px',
  boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px'
})
//#endregion

//#region CellBase

export interface CellBaseOptions {
  openInNewTab?: boolean
  beforeLine?: boolean
  copyToClipboard?: boolean
  icon?: ReactNode
  imageUrl?: string
  imageFallbackSrc?: string
  valueFormatter?: (value?: string) => string
  wrapProps?: BoxProps & { [key: string]: any }
  typographyProps?: TypographyProps & { [key: string]: any }
}

interface CellBaseProps extends CellBaseOptions {
  value?: string
  valueFormatted?: string
}

export const CellBase: FC<CellBaseProps> = (props) => {
  const value = props.valueFormatted ?? props.value
  const isCopyToClipboard = !!props.copyToClipboard && !!value
  return (
    <CellBaseWrap title={value} {...props.wrapProps}>
      {!!props.imageUrl && <ImageSmallFallback imageUri={props.imageUrl} sx={{ mr: '10px' }} imageFallback={props.imageFallbackSrc} />}
      {!!props.icon && props.icon}
      <Typography variant='body2' component='span' noWrap sx={{ flex: 1 }} {...props.typographyProps}>
        {value}
      </Typography>
      {value && props.openInNewTab === true && (
        <Tooltip arrow title='Open new tab'>
          <IconButton component='a' href={value} target='_blank'>
            <OpenInNewIcon fontSize='small' sx={{ color: '#1A75E2' }} />
          </IconButton>
        </Tooltip>
      )}
      {isCopyToClipboard && <CopyToClipboard value={value} />}
      {value && props.beforeLine === true && <Divider flexItem variant='middle' orientation='vertical' />}
    </CellBaseWrap>
  )
}

const CellBaseWrap = styled(({ children, ...props }: BoxProps) => (
  <Box {...props}>
    <div>{children}</div>
  </Box>
))({
  flex: 1,
  width: '100%',
  height: '100%',
  position: 'relative',
  top: 0,
  left: 0,
  '& > div': {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0
  }
})
//#endregion

//#region Chips
export interface CellChipsProps {
  value?: string
  valueFormatter?: (value: string) => string
  separator?: string
  maximum?: number
}

export const CellChips: FC<CellChipsProps> = (props) => {
  if (typeof props.value !== 'string') return props.value ?? <></>

  const list = props.value.split(props?.separator ?? '|').filter((x) => !!x)
  const displayList = props?.maximum ? list.slice(0, props.maximum) : list
  const remainingCount = props?.maximum && list.length > props.maximum ? list.length - props.maximum : 0

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', height: '100%' }}>
      {displayList.map((x, i) => (
        <CustomChip key={'key' + i} label={props.valueFormatter ? props.valueFormatter(x) : x} size='small' />
      ))}
      {remainingCount > 0 && (
        <HelpTooltipWrap
          title='Artists'
          content={
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
              {list.map((x, i) => (
                <CustomChip key={'key' + i} label={x} size='small' />
              ))}
            </Box>
          }
        >
          <CustomChip key='remaining' label={`+${remainingCount}`} size='small' />
        </HelpTooltipWrap>
      )}
    </Box>
  )
}

const CustomChip = styled(Chip)({
  lineHeight: 1
})
//#endregion

//#region Date
export interface CellDatePropsOwner {
  formatString?: string
  showRelative?: boolean
  typographyProps?: TypographyProps
  styledGetter?: (value: any) => SxProps<Theme>
}
export interface CellDateProps extends CellDatePropsOwner {
  value: any
}

export const CellDate: FC<CellDateProps> = (props) => {
  try {
    const showRelative = props?.showRelative ?? false
    const date = dayjsCustom(props.value)
    const formatted = date.format(props.formatString)
    const value = showRelative ? `${formatted} (${dayjsCustom().to(date)})` : formatted
    const sx = props.styledGetter ? props.styledGetter(props.value) : { flex: 1 }
    const mergeTypographyProps = mergeObjects<TypographyProps>({}, props.typographyProps ?? {}, { sx })
    return (
      <Typography variant='body2' component='span' noWrap title={`${value} formatted with ${props.formatString}`} {...mergeTypographyProps}>
        {value}
      </Typography>
    )
  } catch {
    return (
      <Typography variant='body2' component='span' noWrap>
        format invalid
      </Typography>
    )
  }
}
//#endregion
